import { defineStore } from "pinia";
import { reactive, ref, toRaw } from "vue";
import { getEnvVar } from "@/utils/env";
import {
  setPackageQueryData,
  setHotelQueryData,
  setHotelSpaQueryData,
} from "@/utils/query-data";
import { QueryData, LocationData, DateRangeData } from "@/../types/form-data";
import { GuestsData, AcvTokenClaims } from "vacv-components";

export const useQueryStore = (id: string) => {
  return defineStore(id, () => {
    const query = reactive<Partial<QueryData>>({});

    const isDatesValid = ref(false);
    const isGuestsValid = ref(false);
    const isDataValid = ref(true); // assume data from URL is valid by default

    const updateOverallValidity = () => {
      isDataValid.value = isDatesValid.value && isGuestsValid.value;
    };

    const setLocation = (
      value: LocationData,
      type: "departure" | "arrival"
    ) => {
      query[`${type}Location`] = value;
    };

    const setDates = (value: DateRangeData) => {
      query.dateRange = value;
      isDatesValid.value = value.isValid;
      updateOverallValidity();
    };

    const setGuests = (guests: GuestsData) => {
      query.guests = guests;
      isGuestsValid.value = guests.isDataValid;
      updateOverallValidity();
    };

    const setCabinClass = (value: string) => {
      query.cabinClass = value;
    };

    const setLanguage = (value: string) => {
      query.language = value;
    };

    const buildUrlWithQueryParams = (data: object, baseUrlKey: string) => {
      const queryParams = Object.entries(data).map(([key, value]) => [
        key,
        value === undefined ? "" : String(value),
      ]);
      const queryString = new URLSearchParams(toRaw(queryParams));
      const baseUrl = getEnvVar(baseUrlKey);
      return `${baseUrl}?${queryString}`;
    };

    const buildPackageSearchB2BUrl = (sirevsid: string | null) => {
      if (!query.language) {
        return "";
      }
      const lang = query.language.toUpperCase();

      const baseUrl = getEnvVar("VITE_B2B_PACKAGE_SEARCH_PUNCHOUT_URL_" + lang);
      return baseUrl + "?sid=" + sirevsid;
    };

    const buildPackageSearchUrlParams = () => {
      if (
        !(
          query.departureLocation &&
          query.arrivalLocation &&
          query.dateRange &&
          query.guests &&
          query.cabinClass &&
          query.language
        )
      ) {
        return "";
      }

      const lang = query.language.toUpperCase();

      const data = setPackageQueryData(
        query.departureLocation,
        query.arrivalLocation,
        query.dateRange,
        query.guests,
        query.cabinClass,
        query.language
      );

      const packagePunchoutUrl = "VITE_PACKAGE_SEARCH_PUNCHOUT_URL_" + lang;

      return buildUrlWithQueryParams(data, packagePunchoutUrl);
    };

    const buildHotelSearchB2BUrl = (sirevsid: string | null) => {
      if (!query.language) {
        return "";
      }
      const lang = query.language.toUpperCase();
      const baseUrl = getEnvVar("VITE_B2B_HOTEL_SEARCH_PUNCHOUT_URL_" + lang);
      return baseUrl + "?sid=" + sirevsid;
    };

    const buildTTSHotelSearchUrlParams = (
      userJWTInfo: AcvTokenClaims | void,
      isMobile: boolean
    ) => {
      if (
        !(
          query.arrivalLocation &&
          query.dateRange &&
          query.guests &&
          query.language
        )
      ) {
        return "";
      }

      const lang = query.language.toUpperCase();

      const data = setHotelQueryData(
        query.arrivalLocation,
        query.dateRange,
        query.guests,
        query.language,
        userJWTInfo,
        isMobile
      );

      const hotelPunchoutUrl = "VITE_HOTEL_SEARCH_PUNCHOUT_URL_" + lang;

      return buildUrlWithQueryParams(data, hotelPunchoutUrl);
    };

    const buildSpaHotelSearchUrlParams = () => {
      if (
        !(
          query.arrivalLocation &&
          query.dateRange &&
          query.guests &&
          query.language
        )
      ) {
        return "";
      }

      const lang = query.language.toUpperCase();

      const data = setHotelSpaQueryData(
        query.arrivalLocation,
        query.dateRange,
        query.guests,
        query.language
      );

      const hotelSpaPunchoutUrl = "VITE_HOTEL_SEARCH_PUNCHOUT_SPA_URL_" + lang;

      return buildUrlWithQueryParams(data, hotelSpaPunchoutUrl);
    };

    return {
      query,
      isDataValid,
      setLocation,
      setDates,
      setGuests,
      setCabinClass,
      setLanguage,
      buildPackageSearchUrlParams,
      buildPackageSearchB2BUrl,
      buildTTSHotelSearchUrlParams,
      buildHotelSearchB2BUrl,
      buildSpaHotelSearchUrlParams,
    };
  })();
};
